import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'ts-debounce';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { getApiErrorMessage } from 'src/api/axiosClient/errorParser';
import { useAdminCompaniesApi } from '../api';
import {
  FreeCreditsHistoryDialogProps,
  FreeCreditsHistoryDialogViewModel,
  FreeCreditsHistoryEntry,
  FreeCreditsHistoryProps,
  FreeCreditsHistorySort,
  FreeCreditsHistorySortColumn,
  FreeCreditsHistoryViewModel,
} from '../types';

export const FREE_CREDITS_HISTORY_INLINE_LIMIT = 3;
const FREE_CREDITS_HISTORY_PAGE_SIZE = 10;
const DEFAULT_SORT: FreeCreditsHistorySort = 'date_created_desc';

// Text columns read naturally A to Z on the first click, the rest from the largest or newest.
const ASCENDING_FIRST: FreeCreditsHistorySortColumn[] = ['type', 'reason'];

const nextSort = (
  current: FreeCreditsHistorySort,
  column: FreeCreditsHistorySortColumn,
): FreeCreditsHistorySort => {
  if (current === `${column}_asc`) return `${column}_desc`;
  if (current === `${column}_desc`) return `${column}_asc`;
  return ASCENDING_FIRST.includes(column) ? `${column}_asc` : `${column}_desc`;
};

export const useFreeCreditsHistoryController = (
  props: FreeCreditsHistoryProps,
): FreeCreditsHistoryViewModel => {
  const { company, refreshKey } = props;
  const { getCompanyFreeCreditsHistory } = useAdminCompaniesApi();
  const [entries, setEntries] = useState<FreeCreditsHistoryEntry[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [sort, setSort] = useState<FreeCreditsHistorySort>(DEFAULT_SORT);
  const [retryKey, setRetryKey] = useState(0);
  const [isDialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    setEntries([]);
    setCount(0);
  }, [company.id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCompanyFreeCreditsHistory(company.id, {
      sort,
      limit: FREE_CREDITS_HISTORY_INLINE_LIMIT,
      offset: 0,
    })
      .then(response => {
        if (cancelled) return;
        setEntries(response.data.items);
        setCount(response.data.count);
        setFailed(false);
      })
      .catch(e => {
        if (cancelled) return;
        setEntries([]);
        setCount(0);
        setFailed(true);
        reportErrorToUser(e, getApiErrorMessage(e, 'Failed to load free credits history'));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [company.id, refreshKey, retryKey, sort]);

  return {
    company,
    loading,
    failed,
    entries,
    count,
    sort,
    onSortChange: column => setSort(nextSort(sort, column)),
    onRetry: () => setRetryKey(key => key + 1),
    isDialogOpen,
    onOpenDialog: () => setDialogOpen(true),
    onCloseDialog: () => setDialogOpen(false),
  };
};

export const useFreeCreditsHistoryDialogController = (
  props: FreeCreditsHistoryDialogProps,
): FreeCreditsHistoryDialogViewModel => {
  const { open, company, onClose } = props;
  const { getCompanyFreeCreditsHistory } = useAdminCompaniesApi();
  const [entries, setEntries] = useState<FreeCreditsHistoryEntry[]>([]);
  const [count, setCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<FreeCreditsHistorySort>(DEFAULT_SORT);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const applyQuery = useMemo(
    () =>
      debounce((value: string) => {
        setQuery(value.trim());
        setPage(0);
      }, 500),
    [],
  );

  useEffect(() => {
    if (!open) {
      applyQuery.cancel();
      setEntries([]);
      setCount(0);
      setFailed(false);
      setSearchQuery('');
      setQuery('');
      setSort(DEFAULT_SORT);
      setPage(0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getCompanyFreeCreditsHistory(company.id, {
      query: query || undefined,
      sort,
      limit: FREE_CREDITS_HISTORY_PAGE_SIZE,
      offset: page * FREE_CREDITS_HISTORY_PAGE_SIZE,
    })
      .then(response => {
        if (cancelled) return;
        setEntries(response.data.items);
        setCount(response.data.count);
        setFailed(false);
      })
      .catch(e => {
        if (cancelled) return;
        setEntries([]);
        setCount(0);
        setFailed(true);
        reportErrorToUser(e, getApiErrorMessage(e, 'Failed to load free credits history'));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, query, sort, page, retryKey, company.id]);

  const onSearchChange = (value: string) => {
    setSearchQuery(value);
    applyQuery(value);
  };

  const onSortChange = (column: FreeCreditsHistorySortColumn) => {
    setSort(nextSort(sort, column));
    setPage(0);
  };

  return {
    open,
    timezone: company.timezone,
    loading,
    failed,
    entries,
    count,
    searchQuery,
    sort,
    // A failed page leaves no rows to page through; resetting the page itself would refetch the first one.
    page: failed ? 0 : page,
    pageSize: FREE_CREDITS_HISTORY_PAGE_SIZE,
    onSearchChange,
    onSortChange,
    onPageChange: setPage,
    onRetry: () => setRetryKey(key => key + 1),
    close: onClose,
  };
};
